import { useState } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import {
  useAnnounceMaintenanceMutation,
  useCancelMaintenanceMutation,
  useMaintenanceStatusQuery,
} from '@shared/api';

/**
 * Объявить технические работы руками — на случай, когда перезапуск плановый
 * (переезд, обслуживание сервера), а не вызван обновлением: сам деплой
 * объявляет окно за пять минут без чьего-либо участия.
 *
 * Объявление делает две вещи: показывает всем баннер с отсчётом и за две
 * минуты до срока останавливает выдачу новых задач воркерам, чтобы перезапуск
 * не порвал анализ на полуслове.
 */

const Wrap = styled(Card)`
  margin-top: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(5)};
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Field = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Input = styled.input`
  width: 72px;
  padding: 5px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
`;

const TextInput = styled(Input)`
  width: 320px;
  flex: 1;
  min-width: 200px;
`;

const Active = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.warning};
  font-weight: 600;
`;

export function MaintenanceCard() {
  const { data } = useMaintenanceStatusQuery(undefined, { pollingInterval: 10_000 });
  const [announce, { isLoading: announcing }] = useAnnounceMaintenanceMutation();
  const [cancel, { isLoading: cancelling }] = useCancelMaintenanceMutation();
  const [minutes, setMinutes] = useState(5);
  const [message, setMessage] = useState('');

  const left = data?.active ? (data.seconds_until ?? 0) : 0;

  return (
    <Wrap>
      <Title>Технические работы</Title>
      <Hint>
        Объявление показывает всем сотрудникам баннер с отсчётом, а за две минуты до срока агенты
        перестают брать новые задачи — чтобы перезапуск не оборвал идущий анализ. Обновления
        объявляют себя сами; это — для планового обслуживания.
      </Hint>

      {data?.active && (
        <Active>
          {left > 0
            ? `Объявлено: перезапуск через ${Math.floor(left / 60)} мин ${left % 60} с`
            : 'Окно началось — ждём перезапуска'}
        </Active>
      )}

      <Row>
        <Field>
          через
          <Input
            type="number"
            min={1}
            max={720}
            value={minutes}
            onChange={(e) => setMinutes(Math.max(1, Number(e.target.value) || 1))}
          />
          мин
        </Field>
        <TextInput
          type="text"
          placeholder="Текст для баннера (необязательно)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <Button
          type="button"
          disabled={announcing}
          onClick={() =>
            announce({
              in_seconds: minutes * 60,
              message: message.trim() || undefined,
              expected_duration_sec: 120,
            })
          }
        >
          Объявить
        </Button>
        {data?.active && (
          <Button type="button" $variant="ghost" disabled={cancelling} onClick={() => cancel()}>
            Снять
          </Button>
        )}
      </Row>
    </Wrap>
  );
}
